import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Link as LinkIcon, ExternalLink } from "lucide-react";
import Reveal from "../components/common/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { proxied } from "../lib/assets";
import api from "../lib/api";

const SECTION_LABELS = {
  before: { eyebrow: "✈️ Before You Escape", heading: "Curated links & downloads." },
  packing: { eyebrow: "🧳 Packing", heading: "Checklists & essentials." },
  during: { eyebrow: "🌐 During Your Escape", heading: "Stay connected." },
  accessibility: { eyebrow: "🧭 Accessibility", heading: "Advocacy & planning." },
  tips: { eyebrow: "💡 Expert Tips", heading: "Guides worth bookmarking." },
  extras: { eyebrow: "🥂 Extras & Store", heading: "Favorites & recommendations." },
};

const SECTIONS = [
  {
    key: "before",
    eyebrow: "✈️ Before You Escape",
    heading: "Documents, visas & protection.",
    items: [
      {
        q: "Travel Documents & Entry Requirements",
        a: "Make sure your passport, visas, and entry requirements are in order before departure. Passport validity guidelines (many countries require 6 months). Visa requirements by destination. Entry forms and health documentation (when applicable). Pro Tip — don't wait until the last minute; this is one detail that can derail an otherwise perfect escape.",
      },
      {
        q: "Travel Protection (Insurance)",
        a: "Even the most perfectly planned escapes can have unexpected moments. Travel protection helps safeguard your investment and provides peace of mind for trip cancellations or interruptions, medical emergencies abroad, and lost or delayed luggage. Need help choosing coverage? I can guide you to the right option for your trip.",
      },
    ],
  },
  {
    key: "packing",
    eyebrow: "🧳 Packing for Your Escape",
    heading: "Pack with intention.",
    items: [
      {
        q: "Smart Packing Essentials",
        a: "Packing shouldn't feel overwhelming — it should feel intentional. Destination-specific packing checklists, carry-on must-haves, and tips for packing light without sacrificing comfort. Customized packing guidance is included for all Elevated and Signature Escape clients.",
      },
      {
        q: "Travel Gear I Recommend",
        a: "Tried-and-true items that make your journey smoother: quality carry-on luggage, packing cubes for organization, portable chargers & adapters, and comfortable travel accessories. (Amazon affiliate store-front coming soon.)",
      },
    ],
  },
  {
    key: "during",
    eyebrow: "🌐 During Your Escape",
    heading: "Stay connected, travel smooth.",
    items: [
      {
        q: "Staying Connected",
        a: "International phone plans vs. eSIM options. Wi-Fi tips abroad. Important apps to download before departure.",
      },
      {
        q: "Airport & Travel Day Tips",
        a: "Your escape should start the moment you leave home — not at check-in stress. When to arrive at the airport, navigating international terminals, TSA & security tips, and managing layovers smoothly.",
      },
    ],
  },
  {
    key: "accessibility",
    eyebrow: "🧭 Navigate with Confidence",
    heading: "Accessibility & advocacy.",
    items: [
      {
        q: "Accessibility & Special Travel Considerations",
        a: "Every traveler deserves an experience that feels comfortable and supported. With personal experience traveling alongside my high-functioning disabled spouse, I understand how important it is to plan ahead and advocate for your needs — requesting assistance at airports and hotels, mobility considerations, and planning for a smoother, more supported journey. If you have specific needs, I'm here to help you plan with confidence.",
      },
    ],
  },
  {
    key: "tips",
    eyebrow: "💡 Expert Tips",
    heading: "A better escape, every time.",
    items: [
      {
        q: "Book early for the best options and pricing",
        a: "Availability tightens quickly for peak seasons, popular cruises, and milestone travel. Booking early unlocks the best cabins, rooms, and experiences — and protects pricing.",
      },
      {
        q: "Keep digital and printed copies of important documents",
        a: "Passport photo page, visas, insurance details, reservation confirmations. Store them in cloud storage and pack a printed backup.",
      },
      {
        q: "Build in downtime — don't over-schedule",
        a: "The best escapes include room to breathe. We'll plan intentional moments of rest alongside your must-do experiences.",
      },
      {
        q: "Trust your travel advisor (that's me 😉)",
        a: "From the smallest detail to the biggest moment, I'm in your corner — before, during, and after your escape.",
      },
    ],
  },
];

export default function Resources() {
  const [resources, setResources] = useState([]);

  useEffect(() => {
    api.get("/resources").then((r) => setResources(r.data || [])).catch(() => setResources([]));
  }, []);

  // Group by section, preserving section order defined in SECTION_LABELS
  const grouped = Object.keys(SECTION_LABELS).reduce((acc, key) => {
    const items = resources.filter((r) => r.section === key);
    if (items.length) acc[key] = items;
    return acc;
  }, {});

  return (
    <div data-testid="page-resources">
      <section className="relative py-32 lg:py-44 px-6 lg:px-10 bg-nightsky overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          src={proxied("https://customer-assets.emergentagent.com/job_escape-design-lab/artifacts/8xny94cj_0_Nature_Beach_3840x2160.mp4")}
          poster="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="resources-hero-video"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/70" />
        <div className="relative z-10 max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="star-sep mb-8"><span className="text-white">Travel Resources</span></div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.98] text-white" data-testid="resources-title">
              Everything you need<br />for a <span className="italic text-gold">seamless escape.</span>
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={120}>
            <p className="text-lg text-white/85 leading-relaxed font-light">
              A truly unforgettable escape starts long before you arrive at your destination. Prepare, plan, and travel with confidence.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-6 lg:px-10 bg-ivory">
        <div className="max-w-5xl mx-auto space-y-20">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.key} delay={i * 80}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                  <div className="text-xs uppercase tracking-[0.28em] text-gold mb-4">{s.eyebrow}</div>
                  <h2 className="font-serif text-3xl lg:text-4xl leading-tight text-ink">{s.heading}</h2>
                </div>
                <div className="md:col-span-8">
                  <Accordion type="multiple" className="w-full" data-testid={`accordion-${s.key}`}>
                    {s.items.map((it, idx) => (
                      <AccordionItem key={idx} value={`${s.key}-${idx}`} className="border-b border-hairline">
                        <AccordionTrigger className="text-left text-lg font-light text-ink hover:text-gold py-6 [&[data-state=open]]:text-gold" data-testid={`accordion-trigger-${s.key}-${idx}`}>
                          {it.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted text-base font-light leading-[1.85] pb-6">
                          {it.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Admin-curated resources & downloads */}
      {Object.keys(grouped).length > 0 && (
        <section className="py-16 lg:py-24 px-6 lg:px-10 bg-[#F3EDE1]" data-testid="section-curated-resources">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <div className="text-center mb-14">
                <div className="star-sep justify-center mb-6"><span>Curated by Tricia</span></div>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink font-light leading-tight">
                  Links, guides & <span className="italic text-gold">downloads.</span>
                </h2>
                <p className="mt-6 text-lg text-muted font-light max-w-2xl mx-auto">
                  Hand-picked resources I recommend for your escape — updated regularly.
                </p>
              </div>
            </Reveal>
            <div className="space-y-14">
              {Object.entries(grouped).map(([section, items]) => {
                const meta = SECTION_LABELS[section] || { eyebrow: section, heading: "" };
                return (
                  <Reveal key={section}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                      <div className="md:col-span-4">
                        <div className="text-xs uppercase tracking-[0.28em] text-gold mb-3">{meta.eyebrow}</div>
                        <h3 className="font-serif text-2xl lg:text-3xl text-ink leading-tight">{meta.heading}</h3>
                      </div>
                      <div className="md:col-span-8 space-y-3" data-testid={`curated-list-${section}`}>
                        {items.map((r) => (
                          <a
                            key={r.id}
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block border border-hairline bg-ivory p-5 hover:border-gold transition-colors"
                            data-testid={`curated-item-${r.id}`}
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-11 h-11 flex items-center justify-center bg-[#FBF7EC] text-gold shrink-0">
                                {r.resource_type === "pdf" ? (
                                  <FileText size={18} strokeWidth={1.5} />
                                ) : (
                                  <LinkIcon size={18} strokeWidth={1.5} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="font-serif text-lg lg:text-xl text-ink group-hover:text-gold transition-colors leading-tight">
                                    {r.title}
                                  </div>
                                  <ExternalLink size={14} strokeWidth={1.5} className="text-muted group-hover:text-gold shrink-0 mt-1" />
                                </div>
                                {r.description && (
                                  <p className="text-sm text-muted font-light leading-relaxed mt-2">{r.description}</p>
                                )}
                                <div className="text-[10px] uppercase tracking-[0.24em] text-teal mt-2">
                                  {r.resource_type === "pdf" ? "Download PDF" : "Open Resource"}
                                </div>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 lg:py-32 px-6 lg:px-10 bg-nightsky text-ivory">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="star-sep justify-center mb-8"><span>Effortless</span></div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory font-light">
              Want a truly <span className="italic text-gold">effortless</span> escape?
            </h2>
            <p className="mt-8 text-lg text-ivory/70 font-light leading-relaxed">
              These resources are here to help — but you don't have to do it alone. From the smallest details to the biggest moments, I'm here to design an experience that's seamless, personalized, and completely stress-free.
            </p>
            <Link
              to="/plan-your-escape"
              className="mt-10 inline-flex items-center gap-3 h-14 px-10 bg-gold text-ink hover:bg-goldDeep transition-colors text-[12px] uppercase tracking-[0.28em]"
              data-testid="resources-cta"
            >
              Start Planning Your Escape <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
